import { type FC, useState } from 'react'
import classNames from 'classnames'
import { ReactComponent as ZocksLogoFullBlack } from '../../assets/ZocksLogoFullBlack.svg'
import { ReactComponent as CompanyBuildingIcon } from '../../assets/images/sign-up-change-requested-modal-icon.svg'
import { Button } from '../../components/Button/Button'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { logout, selectAuthenticatedUser } from '../../store/auth/authSlice'
import { useSignupChangeMeMutation } from '../../store/signupChange/signupChange'
import styles from './SignUpPageChangeView.module.scss'
import { clearSession } from '../../common/userSession'

export const SignUpPageChangeView: FC = () => {
  const user = useAppSelector(selectAuthenticatedUser)
  const dispatch = useAppDispatch()
  const [signupChangeMe, { isLoading }] = useSignupChangeMeMutation()
  const [error, setError] = useState(false)

  const accountId = user?.accountId

  const onLogoClick = () => {
    dispatch(logout(location))
  }
  const handleContinue = async () => {
    if (!accountId) {
      return
    }

    setError(false)
    try {
      const response = await signupChangeMe({ accountId }).unwrap()
      // Clear the session directly instead of dispatching logout(): logout() sets user to
      // null, which makes the surrounding AuthorizedRoute redirect to the login page — but
      // here we need to send the user to signupChangeLink instead. clearSession() clears the
      // same auth state without a dispatch, so no re-render and no competing redirect.
      // (The full-page navigation below tears down the Redux store either way.)
      clearSession()
      window.location.href = response.data.signupChangeLink
    } catch {
      // Handle the failure inline here rather than via the global error toast: this screen
      // renders outside the layout that hosts the toast container, so a toast would have
      // nowhere to show.
      setError(true)
    }
  }

  return (
    <div className={classNames(styles.page, 'd-flex align-items-center justify-content-center min-vh-100 px-3 zocks-bg-color-gray-100')}>
      <ZocksLogoFullBlack role='button' aria-label='Log out' className={styles.logo} onClick={onLogoClick} />
      <div className={classNames(styles.card, 'zocks-bg-color-white border zocks-border-color-gray-300 d-flex flex-column zocks-gap-3')}>
        <div className='d-flex flex-column align-items-center text-center zocks-gap-1'>
          <CompanyBuildingIcon focusable='false' aria-hidden='true' className={classNames('zocks-text-gray-900', styles.icon)} />
          <div className='d-flex flex-column align-items-center zocks-gap-1.5'>
            <h3 className='zocks-text-gray-900 mb-0'>Your login just changed</h3>
            <p className='paragraph-medium mb-0'>Your firm switched to single sign-on.</p>
            <p className='paragraph-medium mb-0'>
              <span className='fw-bold'>Your old sign-in method no longer works</span> - use your SSO credentials instead.
            </p>
          </div>
          <p className='paragraph-small zocks-text-gray-600 mb-0'>Questions? Contact your firm’s Agency Technology Specialist.</p>
        </div>
        <div className='d-flex flex-column align-items-end zocks-gap-1'>
          <Button variant='primary' loading={isLoading} onClick={handleContinue}>
            Continue to SSO login
          </Button>
          {error ? (
            <div className='d-flex align-items-center justify-content-center text-center zocks-gap-0.5 w-100 py-2 px-3 rounded-2 zocks-bg-color-danger-background zocks-text-danger-dark'>
              <i className='bi bi-exclamation-circle-fill flex-shrink-0' />
              <span className='paragraph-small mb-0'>Something went wrong. If the issue persists, please contact us at support@zocks.io</span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
